if (!customElements.get('gift-message')) {
  customElements.define(
    'gift-message',
    class GiftMessage extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;

        this.modal = document.querySelector(this.dataset.modal);
        this.productForm = document.getElementById(this.dataset.formId);
        if (!this.modal || !this.productForm) return;

        this.initialized = true;
        this.trigger = this.querySelector('[data-gift-trigger]');
        this.summary = this.querySelector('[data-gift-summary]');
        this.status = this.querySelector('[data-gift-status]');
        this.propertyInputs = Object.fromEntries(
          [...this.querySelectorAll('[data-gift-property]')].map((input) => [input.dataset.giftProperty, input])
        );
        this.fields = Object.fromEntries(
          [...this.modal.querySelectorAll('[data-gift-field]')].map((field) => [field.dataset.giftField, field])
        );
        this.timingButtons = [...this.modal.querySelectorAll('[data-gift-timing]')];
        this.timingGroup = this.modal.querySelector('[data-gift-timing-group]');
        this.laterFields = this.modal.querySelector('[data-gift-later-fields]');
        this.help = this.modal.querySelector('[data-gift-help]');
        this.counter = this.modal.querySelector('[data-gift-counter]');
        this.contactError = this.modal.querySelector('[data-gift-contact-error]');
        this.pastError = this.modal.querySelector('[data-gift-past-error]');
        this.saveButton = this.modal.querySelector('[data-gift-save]');
        this.removeButton = this.modal.querySelector('[data-gift-remove]');

        const istanbulNow = this.getIstanbulNow();
        this.fields.date.min = istanbulNow.date;
        this.fields.date.max = this.getMaxDate(istanbulNow);

        this.trigger.addEventListener('click', () => this.prepareDraft());
        this.fields.message.addEventListener('input', () => this.updateCounter());
        this.saveButton.addEventListener('click', () => this.save());
        this.removeButton.addEventListener('click', () => this.remove());
        this.timingButtons.forEach((button) => {
          button.addEventListener('click', () => this.selectTiming(button.dataset.giftTiming));
          button.addEventListener('keydown', (event) => this.onTimingKeydown(event));
        });
        Object.values(this.fields).forEach((field) => {
          field.addEventListener('input', () => this.clearFieldError(field));
          field.addEventListener('change', () => this.clearFieldError(field));
        });

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
            const variantInput = this.productForm.querySelector('[name="id"]');
            if (
              event.source === 'product-form' &&
              variantInput &&
              event.productVariantId?.toString() === variantInput.value.toString()
            ) {
              this.resetSavedState();
            }
          });
        }
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
      }

      getIstanbulNow() {
        const parts = new Intl.DateTimeFormat('en-CA', {
          timeZone: 'Europe/Istanbul',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hourCycle: 'h23',
        })
          .formatToParts(new Date())
          .reduce((result, part) => {
            result[part.type] = part.value;
            return result;
          }, {});

        return {
          year: Number(parts.year),
          month: Number(parts.month),
          day: Number(parts.day),
          date: `${parts.year}-${parts.month}-${parts.day}`,
          time: `${parts.hour}:${parts.minute}`,
        };
      }

      getMaxDate(now) {
        const maximum = new Date(Date.UTC(now.year, now.month - 1 + 6, now.day));
        return maximum.toISOString().slice(0, 10);
      }

      prepareDraft() {
        Object.entries(this.fields).forEach(([key, field]) => {
          field.value = this.propertyInputs[key]?.disabled ? '' : this.propertyInputs[key]?.value || '';
        });

        const timingValue = this.propertyInputs.timing.disabled ? '' : this.propertyInputs.timing.value;
        const savedTiming = this.timingButtons.find((button) => button.dataset.propertyValue === timingValue);
        this.selectTiming(savedTiming?.dataset.giftTiming || 'delivery', false);
        this.removeButton.hidden = !this.hasSavedMessage();
        this.updateCounter();
        this.clearErrors();
      }

      selectTiming(timing, focus = false) {
        this.selectedTiming = timing;
        this.timingButtons.forEach((button) => {
          const selected = button.dataset.giftTiming === timing;
          button.setAttribute('aria-pressed', selected.toString());
          button.setAttribute('aria-selected', selected.toString());
          button.tabIndex = selected ? 0 : -1;
          if (selected) {
            this.help.textContent = button.dataset.help;
            if (focus) button.focus();
          }
        });

        const later = timing === 'later';
        this.laterFields.hidden = !later;
        this.fields.date.required = later;
        this.fields.time.required = later;
        if (later && !this.fields.date.value) this.fields.date.value = this.getIstanbulNow().date;
        if (!later) {
          this.fields.date.value = '';
          this.fields.time.value = '';
          this.pastError.hidden = true;
          this.clearFieldError(this.fields.date);
          this.clearFieldError(this.fields.time);
        }
      }

      onTimingKeydown(event) {
        if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
        event.preventDefault();
        const currentIndex = this.timingButtons.indexOf(event.currentTarget);
        const direction = ['ArrowRight', 'ArrowDown'].includes(event.key) ? 1 : -1;
        const nextIndex = (currentIndex + direction + this.timingButtons.length) % this.timingButtons.length;
        this.selectTiming(this.timingButtons[nextIndex].dataset.giftTiming, true);
      }

      updateCounter() {
        this.counter.textContent = `${this.fields.message.value.length}/210`;
      }

      clearErrors() {
        Object.values(this.fields).forEach((field) => this.clearFieldError(field));
        this.contactError.hidden = true;
        this.pastError.hidden = true;
      }

      clearFieldError(field) {
        field.removeAttribute('aria-invalid');
        if (field === this.fields.email || field === this.fields.phone) this.contactError.hidden = true;
        if (field === this.fields.date || field === this.fields.time) this.pastError.hidden = true;
      }

      validate() {
        this.clearErrors();
        const requiredFields = [this.fields.recipient, this.fields.sender, this.fields.message];
        if (this.selectedTiming === 'later') requiredFields.push(this.fields.date, this.fields.time);

        let firstInvalid;
        requiredFields.forEach((field) => {
          if (!field.checkValidity()) {
            field.setAttribute('aria-invalid', 'true');
            firstInvalid ||= field;
          }
        });

        if (this.fields.email.value && !this.fields.email.checkValidity()) {
          this.fields.email.setAttribute('aria-invalid', 'true');
          firstInvalid ||= this.fields.email;
        }

        if (!this.fields.email.value.trim() && !this.fields.phone.value.trim()) {
          this.fields.email.setAttribute('aria-invalid', 'true');
          this.fields.phone.setAttribute('aria-invalid', 'true');
          this.contactError.textContent = this.dataset.contactError;
          this.contactError.hidden = false;
          firstInvalid ||= this.fields.email;
        }

        if (this.selectedTiming === 'later' && this.fields.date.value && this.fields.time.value) {
          const now = this.getIstanbulNow();
          const selected = `${this.fields.date.value}T${this.fields.time.value}`;
          const current = `${now.date}T${now.time}`;
          if (selected <= current) {
            this.fields.date.setAttribute('aria-invalid', 'true');
            this.fields.time.setAttribute('aria-invalid', 'true');
            this.pastError.textContent = this.dataset.pastError;
            this.pastError.hidden = false;
            firstInvalid ||= this.fields.time;
          }
        }

        if (firstInvalid) {
          firstInvalid.focus();
          firstInvalid.reportValidity();
          return false;
        }

        return true;
      }

      save() {
        if (!this.validate()) return;

        const selectedButton = this.timingButtons.find((button) => button.dataset.giftTiming === this.selectedTiming);
        const values = {
          recipient: this.fields.recipient.value.trim(),
          sender: this.fields.sender.value.trim(),
          message: this.fields.message.value.trim(),
          email: this.fields.email.value.trim(),
          phone: this.fields.phone.value.trim(),
          timing: selectedButton.dataset.propertyValue,
          date: this.selectedTiming === 'later' ? this.fields.date.value : '',
          time: this.selectedTiming === 'later' ? this.fields.time.value : '',
          timezone: this.selectedTiming === 'later' ? 'Europe/Istanbul' : '',
        };

        Object.entries(this.propertyInputs).forEach(([key, input]) => {
          input.value = values[key] || '';
          input.disabled = !values[key];
        });

        this.trigger.textContent = this.dataset.triggerSaved;
        this.summary.textContent = this.dataset.summarySaved;
        this.summary.hidden = false;
        this.removeButton.hidden = false;
        this.status.textContent = this.dataset.statusSaved;
        this.modal.hide();
      }

      remove() {
        this.resetSavedState();
        this.status.textContent = this.dataset.statusRemoved;
        this.modal.hide();
      }

      hasSavedMessage() {
        return Boolean(this.propertyInputs.message && !this.propertyInputs.message.disabled && this.propertyInputs.message.value);
      }

      resetSavedState() {
        Object.values(this.propertyInputs).forEach((input) => {
          if (input.dataset.giftProperty !== 'timezone') input.value = '';
          input.disabled = true;
        });
        Object.values(this.fields).forEach((field) => (field.value = ''));
        this.selectTiming('delivery', false);
        this.trigger.textContent = this.dataset.triggerDefault;
        this.summary.textContent = '';
        this.summary.hidden = true;
        this.removeButton.hidden = true;
        this.updateCounter();
        this.clearErrors();
      }
    }
  );
}
